import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type {
  WebSocketConnectionStatus,
  SubscriptionTarget,
  PendingNotification,
  SpeedTestState,
} from '@/app/websocket/types';

// Server-pushed warning that the user's session is about to be evicted
// for HTTP inactivity. Seeds a UI countdown; cleared when the user clicks
// Stay (and the refresh request succeeds) or when they log out.
export interface SessionExpiringState {
  // Server-reported seconds remaining when the push arrived.
  secondsUntilLogout: number;
  // Wall-clock time the message was received. Combined with
  // secondsUntilLogout, lets the UI compute the absolute deadline.
  receivedAt: number;
}

export interface WebSocketState {
  status: WebSocketConnectionStatus;
  subscriptions: SubscriptionTarget[];
  pendingChanges: PendingNotification[];
  speedTest: SpeedTestState;
  lastError: string;
  sessionExpiring: SessionExpiringState | null;
}

const initialSpeedTest: SpeedTestState = {
  status: 'idle',
};

const initialState: WebSocketState = {
  status: 'disconnected',
  subscriptions: [],
  pendingChanges: [],
  speedTest: initialSpeedTest,
  lastError: '',
  sessionExpiring: null,
};

function subscriptionsMatch(a: SubscriptionTarget, b: SubscriptionTarget): boolean {
  return a.type === b.type && (a.scope_id ?? '') === (b.scope_id ?? '');
}

export const websocketSlice = createSlice({
  name: 'websocket',
  initialState,
  reducers: {
    setStatus(state, action: PayloadAction<WebSocketConnectionStatus>) {
      state.status = action.payload;
    },

    addSubscription(state, action: PayloadAction<SubscriptionTarget>) {
      const target = action.payload;
      const exists = state.subscriptions.some((s) => subscriptionsMatch(s, target));
      if (!exists) {
        state.subscriptions.push(target);
      }
    },

    removeSubscription(state, action: PayloadAction<SubscriptionTarget>) {
      const target = action.payload;
      state.subscriptions = state.subscriptions.filter(
        (s) => !subscriptionsMatch(s, target)
      );
    },

    clearSubscriptions(state) {
      state.subscriptions = [];
    },

    addPendingChange(state, action: PayloadAction<PendingNotification>) {
      const notification = action.payload;
      // Deduplicate by type + targetId — replace existing with newer
      const idx = state.pendingChanges.findIndex(
        (n) => n.type === notification.type && n.targetId === notification.targetId
      );
      if (idx >= 0) {
        state.pendingChanges[idx] = notification;
      } else {
        state.pendingChanges.push(notification);
      }
    },

    clearPendingChanges(state) {
      state.pendingChanges = [];
    },

    dismissPendingChange(state, action: PayloadAction<string>) {
      const type = action.payload;
      state.pendingChanges = state.pendingChanges.filter((n) => n.type !== type);
    },

    setSpeedTest(state, action: PayloadAction<SpeedTestState>) {
      state.speedTest = action.payload;
    },

    clearSpeedTest(state) {
      state.speedTest = initialSpeedTest;
    },

    setLastError(state, action: PayloadAction<string>) {
      state.lastError = action.payload;
    },

    setSessionExpiring(state, action: PayloadAction<SessionExpiringState>) {
      state.sessionExpiring = action.payload;
    },

    clearSessionExpiring(state) {
      state.sessionExpiring = null;
    },

    resetState() {
      return initialState;
    },
  },
});

export default websocketSlice;
